import { useContext, useEffect, useState } from "react";
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { AuthContext } from "../context/AuthProvider";
import CustomDataGrid from "../components/CustomDataGrid";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import ConfirmDialog from "../components/ConfirmDialog";

const DEFAULT_FILTER = {
  name: "",
};

const Categories = () => {
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [isLoading, setIsLoading] = useState(false);

  const [rows, setRows] = useState([]);
  const [rowId, setRowId] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);

  const [name, setName] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const authContext = useContext(AuthContext);

  const handleReset = () => {
    setFilter(DEFAULT_FILTER);
  };

  const onDialogClose = () => {
    setDialogOpen(false);
    setRowId(null);
    setName("");
  };

  const onEdit = (row) => {
    setRowId(row.ID);
    setName(row.Name);
    setDialogOpen(true);
  };

  const onMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setRowId(row.ID);
  };

  const onMenuClose = () => {
    setAnchorEl(null);
  };

  const onDelete = () => {
    setConfirmDialogOpen(true);
    onMenuClose();
  };

  const onCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setRowId(null);
  };

  const onConfirmDelete = async () => {
    try {
      const response = await fetchWithInterceptor(
        `/api/categories/delete/${rowId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        },
      );
      if (response.ok) {
        setRows((prevRows) => prevRows.filter((r) => r.ID !== rowId));
        setRowId(null);
        setConfirmDialogOpen(false);
      } else {
        const error = await response.json();
        console.error("Failed to delete category", error);
        authContext.showMessage(
          error?.error || "Failed to delete category",
          "error",
        );
      }
    } catch (error) {
      console.error("Failed to delete category", error);
      authContext.showMessage("Failed to delete category", "error");
    }
  };

  const loadData = async () => {
    setIsLoading(true);

    try {
      const response = await fetchWithInterceptor(
        `/api/categories/list`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setRows(data);
      } else {
        const error = await response.json();
        console.error("Failed to load categories", error);
        authContext.showMessage(
          error?.error || "Failed to load categories",
          "error",
        );
      }
    } catch (error) {
      console.error("Failed to load categories", error);
      authContext.showMessage("Failed to load categories", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = async () => {
    try {
      const response = await fetchWithInterceptor(
        rowId
          ? `/api/categories/update/${rowId}`
          : "/api/categories/create",
        {
          method: rowId ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
          body: JSON.stringify({ name }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setRows((prevRows) => {
          const newRows = [...prevRows];
          if (rowId) {
            const index = newRows.findIndex((r) => r.ID === rowId);
            if (index !== -1) {
              newRows[index] = data;
            }
          } else {
            newRows.unshift(data);
          }
          return newRows;
        });
        onDialogClose();
      } else {
        const error = await response.json();
        console.error("Failed to save category", error);
        authContext.showMessage(
          error?.error || "Failed to save category",
          "error",
        );
      }
    } catch (error) {
      console.error("Failed to save category", error);
      authContext.showMessage("Failed to save category", "error");
    }
  };

  useEffect(() => {
    loadData();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      field: "Name",
      headerName: "Name",
      flex: 1,
      sortable: true,
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 150,
      renderCell: (params) => (
        <>
          <Typography
            variant="body2"
            sx={{
              color: "primary.main",
              cursor: "pointer",
              display: "inline-block",
              mr: 1,
            }}
            onClick={() => onEdit(params.row)}
          >
            EDIT
          </Typography>
          <IconButton size="small" onClick={(e) => onMenuOpen(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const filteredRows = rows.filter((r) =>
    r.Name.toLowerCase().includes(filter.name.toLowerCase()),
  );

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Create, edit, and delete categories (e.g., "C7-USA-RED"). Changes made here will be consumed by other modules, and we will create the category in Cin7 too.
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper
          sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}
        >
          <div style={{ padding: 16 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="Name"
                  placeholder="Eg: C7-ARG-RED"
                  variant="outlined"
                  value={filter.name}
                  onChange={(e) =>
                    setFilter({ ...filter, name: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6, md: "auto" }}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button onClick={handleReset}>RESET FILTER</Button>
              </Grid>
            </Grid>
          </div>
          <Divider />

          <Grid
            container
            sx={{ px: 4, pb: 2, pt: 2, justifyContent: "flex-end" }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setRowId(null);
                setDialogOpen(true);
              }}
            >
              CREATE CATEGORY
            </Button>
          </Grid>

          <CustomDataGrid
            rows={filteredRows}
            columns={columns}
            // rowCount={filteredRows.length}
            loading={isLoading}
            getRowId={(row) => row.ID}
            autoHeight
            pageSize={20}
            paginationMode="client"
            pageSizeOptions={[20, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20,
                },
              },
            }}
          />
        </Paper>
      </Container>

      <Dialog open={dialogOpen} onClose={onDialogClose}>
        <DialogTitle>
          {rowId ? "Edit Category" : "Create Category"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {rowId
              ? "Edit the name of the category and click Save to apply changes."
              : "Enter the name of the new category and click Save to create it."}
          </DialogContentText>
          <TextField
            autoFocus
            required
            label="Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={!name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={onMenuClose}>
        <MenuItem onClick={onDelete}>Delete</MenuItem>
      </Menu>

      <ConfirmDialog
        open={confirmDialogOpen}
        title="Confirm Deletion"
        message="Are you sure you want to delete this category? This action cannot be undone."
        onCancel={onCloseConfirmDialog}
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default Categories;
